import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { glyphGroup, type GlyphId } from '../../../components/glyphs';
import { draw, travel } from '../../../components/svg';
import { words, revealWords, settleWords } from '../../../components/text';
import { productReveal, playProductReveal, settleProductReveal } from '../../../components/reveal';

/** One product and what stays connected to it (Prompt2 §16). */
const JOURNEY: ReadonlyArray<{ label: string; glyph: GlyphId }> = [
  { label: 'Product', glyph: 'phone' },
  { label: 'Ownership', glyph: 'customer' },
  { label: 'Warranty', glyph: 'warranty' },
  { label: 'Service', glyph: 'wrench' },
];
const Y = 520;
const nodeX = (i: number): number => 350 + i * 300;

/**
 * Selected work: one compact scene.
 * 0 eTrust, and a single product
 * 1 Ownership, warranty and service stay connected to it
 * 2 ONE CONNECTED PRODUCT JOURNEY.
 */
export class ETrustScene extends BaseScene {
  public readonly stepCount = 3;
  private moving = false;

  protected template(): string {
    const nodes = JOURNEY.map((j, i) => `
      <g class="et-node" data-index="${i}">
        ${i > 0 ? `<path class="et-link draw" pathLength="1" d="M${nodeX(i - 1) + 62} ${Y} H${nodeX(i) - 62}"/>` : ''}
        <g transform="translate(${nodeX(i)} ${Y})"><g class="et-node__motion">
          <circle class="et-node__ring" r="52"/>
          <g class="et-node__glyph">${glyphGroup(j.glyph, 40)}</g>
          <text class="et-node__label" y="96" text-anchor="middle">${j.label.toUpperCase()}</text>
        </g></g>
      </g>`).join('');

    return `
      <div class="scene-layout etrust">
        <svg class="diagram et-diagram" viewBox="0 0 1600 900" role="img" aria-label="One product with its ownership, warranty and service connected">
          <path class="et-track" d="M${nodeX(0)} ${Y} H${nodeX(JOURNEY.length - 1)}"/>
          ${nodes}
          <circle class="et-token" r="7"/>
        </svg>
        ${productReveal('etrust', 0, 'product-reveal--top')}
        <h2 class="statement statement--accent et-statement" data-from="2">${words('ONE CONNECTED PRODUCT JOURNEY.')}</h2>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    playProductReveal(this.root, tl, 0.1);
    tl.fromTo(this.$('.et-node[data-index="0"] .et-node__motion'), { opacity: 0, scale: 0.7, transformOrigin: '50% 50%' }, { opacity: 1, scale: 1, duration: 0.8, ease: 'back.out(1.8)' }, 0.8);
  }

  protected onExit(): void {
    this.moving = false;
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode(this.meta.id, step, immediate);
    const forward = !immediate && step > previousStep;
    const tl = this.timeline();
    settleProductReveal(this.root);
    settleWords(this.$('.et-statement'));

    const motions = this.$$('.et-node__motion');
    const links = this.$$('.et-link');
    if (forward && step === 1) {
      draw(links, false, { immediate: true });
      links.forEach((link, i) => {
        tl.add(draw(link, true, { duration: 0.5, ease: 'power2.out' }), 0.2 + i * 0.5);
        tl.fromTo(motions[i + 1], { opacity: 0, scale: 0.7, transformOrigin: '50% 50%' }, { opacity: 1, scale: 1, duration: 0.6, ease: 'back.out(2)' }, 0.55 + i * 0.5);
      });
    } else {
      draw(links, step >= 1, { immediate: true });
      motions.forEach((m, i) => gsap.set(m, { opacity: i === 0 || step >= 1 ? 1 : 0, scale: 1, transformOrigin: '50% 50%' }));
    }
    if (forward && step === 2) tl.add(revealWords(this.$('.et-statement')), 0.2);

    const on = step >= 1;
    if (on !== this.moving) {
      this.moving = on;
      this.stopLoops();
      const token = this.$<SVGCircleElement>('.et-token');
      token.style.opacity = on ? '1' : '0';
      if (on) {
        const loop = travel(token, this.$<SVGPathElement>('.et-track'), 3.2, forward ? 2 : 0);
        this.loop(loop);
      }
    }
  }
}
